import React, { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Rocket, Clock, Zap, Users, Wallet, Shield, X, ArrowRight, Download } from 'lucide-react'
import { walletService } from '../services/walletService'
import { useLanguage } from '../i18n'
import LanguageSwitcher from './LanguageSwitcher'

const Navigation = ({ isConnected, userAddress, balance, onConnect, onDisconnect, currentWalletType }) => {
  const { t } = useLanguage()
  const [showWalletSelector, setShowWalletSelector] = useState(false)
  const [availableWallets, setAvailableWallets] = useState([])
  const [connectingWallet, setConnectingWallet] = useState(null)
  const [error, setError] = useState(null)

  const navItems = [
    { path: '/', label: t('nav.home'), icon: Rocket },
    { path: '/mining', label: t('nav.mining'), icon: Clock },
    { path: '/fuel', label: t('nav.fuel'), icon: Zap },
    { path: '/team', label: t('nav.team'), icon: Users },
    { path: '/assets', label: t('nav.assets'), icon: Wallet },
  ]

  const openWalletSelector = () => {
    const wallets = walletService.getAvailableWallets()
    setAvailableWallets(wallets)
    setError(null)
    setConnectingWallet(null)
    setShowWalletSelector(true)
  }

  const handleWalletConnect = async (walletType) => {
    try {
      setConnectingWallet(walletType)
      setError(null)
      await walletService.connectWallet(walletType)
      setShowWalletSelector(false)
      onConnect()
    } catch (err) {
      setError(err.message)
      setConnectingWallet(null)
    }
  }

  return (
    <>
      {/* 桌面端导航栏 */}
      <nav className="tech-header hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-8">
              <NavLink to="/" className="flex items-center space-x-3">
                <div className="flex items-center space-x-3">
                  <Rocket className="w-8 h-8 text-titanium-gold" />
                  <div className="tech-title text-2xl">{t('home.title').split(' ')[0]}</div>
                </div>
              </NavLink>

              <div className="hidden md:flex items-center space-x-2">
                {navItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                          isActive
                            ? 'bg-gradient-to-r from-titanium-gold/20 to-gold-light/20 text-titanium-gold border border-titanium-gold/30'
                            : 'text-gray-300 hover:text-titanium-gold hover:bg-titanium-gold/10'
                        }`
                      }
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-semibold">{item.label}</span>
                    </NavLink>
                  )
                })}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              
              {isConnected ? (
                <>
                  <div className="connection-status connected">
                    <Shield className="w-4 h-4" />
                    <span className="font-mono">
                      {userAddress.slice(0, 6)}...{userAddress.slice(-4)}
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-titanium-gold bg-gradient-to-r from-titanium-gold/10 to-gold-light/10 px-3 py-2 rounded-lg">
                    {balance} STORM
                  </div>
                  <button
                    onClick={onDisconnect}
                    className="btn-neon text-sm"
                  >
                    {t('common.disconnect')}
                  </button>
                </>
              ) : (
                <button
                  onClick={openWalletSelector}
                  className="wallet-connect-btn"
                >
                  <Wallet className="w-5 h-5 mr-2" />
                  {t('common.connectWallet')}
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* 移动端顶部钱包栏 */}
      <div className="mobile-wallet-bar md:hidden">
        <div className="flex items-center space-x-2">
          <Rocket className="w-6 h-6 text-titanium-gold" />
          <span className="tech-title text-lg">{t('home.title').split(' ')[0]}</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <LanguageSwitcher />
          
          {isConnected ? (
            <div className="flex items-center space-x-2">
              <div className="text-xs font-semibold text-titanium-gold bg-gradient-to-r from-titanium-gold/10 to-gold-light/10 px-2 py-1 rounded-lg">
                {balance} STORM
              </div>
              <button
                onClick={onDisconnect}
                className="text-xs px-3 py-1 bg-red-500/20 text-red-400 rounded-lg"
              >
                {t('common.disconnect')}
              </button>
            </div>
          ) : (
            <button
              onClick={openWalletSelector}
              className="text-xs px-3 py-1.5 bg-gradient-to-r from-titanium-gold to-gold-light text-black font-semibold rounded-lg"
            >
              {t('common.connectWallet')}
            </button>
          )}
        </div>
      </div>

      {/* 移动端底部导航栏 */}
      <div className="mobile-bottom-nav md:hidden">
        <div className="flex items-center justify-around">
          {navItems.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `mobile-nav-item ${isActive ? 'active' : ''}`
                }
              >
                <Icon />
                <span>{item.label}</span>
              </NavLink>
            )
          })}
        </div>
      </div>

      {/* 钱包选择器弹窗 */}
      {showWalletSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowWalletSelector(false)} />
          <div className="relative bg-gradient-to-br from-slate-900/95 to-slate-800/95 border border-titanium-gold/30 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Wallet className="w-6 h-6 text-titanium-gold" />
                  <h2 className="text-2xl font-bold text-white">{t('wallet.title')}</h2>
                </div>
                <button onClick={() => setShowWalletSelector(false)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              )}

              <div className="space-y-3">
                {availableWallets.map((wallet) => (
                  <button
                    key={wallet.type}
                    onClick={() => !wallet.notInstalled && handleWalletConnect(wallet.type)}
                    disabled={connectingWallet !== null}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
                      wallet.notInstalled
                        ? 'border-gray-600 bg-gray-800/50 cursor-not-allowed opacity-60'
                        : 'border-titanium-gold/30 bg-slate-800/50 hover:border-titanium-gold/60 hover:bg-slate-700/50 cursor-pointer'
                    } ${connectingWallet === wallet.type ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <div className="flex items-center space-x-4">
                      <span className="text-3xl">{wallet.icon}</span>
                      <div className="text-left">
                        <p className="font-semibold text-white">{wallet.name}</p>
                        {wallet.notInstalled ? (
                          <p className="text-xs text-gray-400">{t('wallet.notInstalled')}</p>
                        ) : (
                          <p className="text-xs text-green-400">{t('wallet.detected')}</p>
                        )}
                      </div>
                    </div>
                    {wallet.notInstalled ? (
                      <a
                        href={wallet.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center space-x-2 px-4 py-2 bg-titanium-gold/20 border border-titanium-gold/50 rounded-lg hover:bg-titanium-gold/30 transition-colors"
                      >
                        <Download className="w-4 h-4 text-titanium-gold" />
                        <span className="text-sm font-medium text-titanium-gold">{t('wallet.install')}</span>
                      </a>
                    ) : connectingWallet === wallet.type ? (
                      <div className="w-5 h-5 border-2 border-titanium-gold/30 border-t-titanium-gold rounded-full animate-spin" />
                    ) : (
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-700">
                <p className="text-center text-xs text-gray-500">{t('wallet.terms')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Navigation
